import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MdSearch, MdClose } from "react-icons/md";
import Header from "../../Header";
import LeftNavBar from "../../LeftNavBar";
import PageHeader from "../../PageHeader";
import SlidingModal from "../../SlidingModal/slidingModal";
import PopupModal from "../../PopupModal";
import { PageHeaderName } from "../../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import JobsTable from "../JobsTable";
import HiringOverview from "../HiringOverview";
import CreateJobModal from "../CreateJobModal";
import MyReferralsList from "../MyReferralsList";
import { normalizeJob } from "../../../util/hiringUtils";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES, HIRING_MANAGEMENT_FEATURE_CODES } from "../../../util/featureCodes";
import {
  getHiringJobs,
  getHiringJobById,
  createHiringJob,
  updateHiringJob,
  updateHiringJobStatus,
  deleteHiringJob,
} from "../../../api/services/hroneApi";
import "./index.css";

/* ── Tab definitions ──────────────────────────────────────────────────
   Two tiers: top-level (Job Openings / All Referrals / My Referrals) and,
   only under "Job Openings", the job-status sub-tabs (Open/Closed/Draft). */
const TOP_TABS = [
  { id: "JOBS",          label: "Job Openings",  path: "/hiring/open-jobs" },
  { id: "ALL_REFERRALS", label: "All Referrals", path: "/hiring/all-referrals" },
  { id: "MY_REFERRALS",  label: "My Referrals",  path: "/hiring/my-referrals" },
];

const getTopTabFromPath = (pathname) => {
  if (pathname.includes("/hiring/all-referrals")) return "ALL_REFERRALS";
  if (pathname.includes("/hiring/my-referrals"))  return "MY_REFERRALS";
  return "JOBS";
};

const PAGE_TABS = [
  { id: "OPEN",   label: "Open Jobs",   path: "/hiring/open-jobs" },
  { id: "CLOSED", label: "Closed Jobs", path: "/hiring/closed-jobs" },
  { id: "DRAFT",  label: "Draft Jobs",  path: "/hiring/draft-jobs" },
];

const getTabFromPath = (pathname) => {
  if (pathname.includes("/hiring/closed-jobs")) return "CLOSED";
  if (pathname.includes("/hiring/draft-jobs"))  return "DRAFT";
  return "OPEN";
};

const EMPTY_STATE_COPY = {
  OPEN:   { title: "No Open Jobs Found",   message: "Create and publish a job to see it here." },
  DRAFT:  { title: "No Draft Jobs Found",  message: "Save a job as draft to see it here." },
  CLOSED: { title: "No Closed Jobs Found", message: "Closed jobs will appear here." },
};

const Hiring = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [jobs, setJobs]         = useState([]);
  const [counts, setCounts]     = useState({ OPEN: 0, CLOSED: 0, DRAFT: 0 });
  const [loading, setLoading]   = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const [topTab, setTopTab]                     = useState(() => getTopTabFromPath(location.pathname));
  const [activeTab, setActiveTab]               = useState(() => getTabFromPath(location.pathname));
  const [searchQuery, setSearchQuery]           = useState("");
  // Mirrored from HiringOverview so the job table uses the exact same
  // published_date window as the dashboard cards above.
  const [overviewDateRange, setOverviewDateRange] = useState({ fromDate: null, toDate: null });

  const { hasPermission, hasAnyPermission } = usePermissions();
  const canCreateJob = hasPermission(FEATURE_CODES.HIRING_JOB_CREATE);
  // A role with only default Hiring access (no management permission) never
  // sees the HR console — it gets the employee job-openings screen instead.
  const hasHiringManagement = hasAnyPermission(HIRING_MANAGEMENT_FEATURE_CODES);
  // The org-wide "All Referrals" list is gated on hiring.view (backend
  // authorizes it the same way). That is a default permission every role
  // holds, so this is effectively always true; kept as a check so a role that
  // ever has it removed also loses the tab instead of hitting a 403.
  // "My Referrals" is self-scoped and always available.
  const canSeeAllReferrals = hasPermission(FEATURE_CODES.HIRING_VIEW);
  const visibleTopTabs = canSeeAllReferrals
    ? TOP_TABS
    : TOP_TABS.filter((tab) => tab.id !== "ALL_REFERRALS");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editJob, setEditJob]                     = useState(null);
  const [confirmModal, setConfirmModal]           = useState({ open: false, type: null, job: null });
  const [errorModal, setErrorModal]               = useState({ open: false, message: "" });

  const orgDetails   = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#2563EB";

  const triggerRefresh = () => setRefreshTick((t) => t + 1);

  const handleOverviewDateRangeChange = useCallback(({ fromDate, toDate }) => {
    setOverviewDateRange((prev) => {
      if (prev.fromDate === fromDate && prev.toDate === toDate) return prev;
      return { fromDate, toDate };
    });
  }, []);

  /* ── Sync active tab from URL (date range stays owned by Hiring Overview) ── */
  useEffect(() => {
    setTopTab(getTopTabFromPath(location.pathname));
    setActiveTab(getTabFromPath(location.pathname));
    setSearchQuery("");
  }, [location.pathname]);

  /* ── Fetch jobs from API using Overview's date window + status tab.
     search is applied client-side below. ── */
  useEffect(() => {
    if (topTab !== "JOBS") return;

    const { fromDate, toDate } = overviewDateRange;
    // Wait until Overview has published a resolved range before hitting the
    // jobs API (initial mount). Incomplete custom range clears the table.
    if (fromDate == null && toDate == null) {
      setLoading(true);
      return undefined;
    }
    if (!fromDate || !toDate) {
      setJobs([]);
      setCounts({ OPEN: 0, CLOSED: 0, DRAFT: 0 });
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Drafts are saved with published_date = NULL (they haven't been
        // published yet), and the backend filters this list with
        // "published_date BETWEEN from_date AND to_date" — NULL never
        // matches a BETWEEN range, so applying the Overview's date window
        // to the Draft tab would silently hide every draft (and zero out
        // its count) no matter how many actually exist. Drafts aren't tied
        // to a publish date, so skip the date filter for that tab only.
        const params = activeTab === "DRAFT"
          ? { status: activeTab }
          : { status: activeTab, from_date: fromDate, to_date: toDate };
        const res  = await getHiringJobs(params);
        const data = await res.json();
        if (!cancelled) {
          if (data.status) {
            setJobs(data.data.jobs.map(normalizeJob));
            // Only trust the count for the status we actually asked for —
            // see the tab-counts effect below for why the other two values
            // in this same response can't be relied on.
            setCounts((prev) => ({
              ...prev,
              [activeTab]: data.data.counts?.[activeTab] ?? prev[activeTab],
            }));
          } else {
            toast.error(data.message || "Failed to load jobs");
          }
        }
      } catch {
        if (!cancelled) toast.error("Failed to load jobs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [topTab, activeTab, overviewDateRange, refreshTick]);

  /* ── Tab badge counts ─────────────────────────────────────────────────
     The backend appears to pick its date field (published_date vs.
     closed_date, etc.) based on the requested "status", then computes ALL
     THREE tab counts in that response using that one field/filter. That
     makes cross-tab values inside a single response unreliable — e.g. a
     request for status=OPEN can report a "closed" count filtered by
     published_date, while status=CLOSED reports it filtered by closed_date,
     and status=DRAFT (no date filter) reports the true unfiltered total.
     Observed effect: the same "Closed" badge showed 0 / 3 / 4 depending on
     which tab happened to be open when it was fetched.

     To keep every badge accurate regardless of which tab is active, fetch
     each status's count from its own dedicated request (mirroring exactly
     what that tab would query) and only ever read counts[status] from the
     response where "status" was what we actually requested. */
  useEffect(() => {
    if (topTab !== "JOBS") return;
    const { fromDate, toDate } = overviewDateRange;
    if (!fromDate || !toDate) return undefined;

    let cancelled = false;
    const load = async () => {
      const results = await Promise.all(
        PAGE_TABS.map(({ id }) => {
          const params = id === "DRAFT"
            ? { status: id, limit: 1 }
            : { status: id, from_date: fromDate, to_date: toDate, limit: 1 };
          return getHiringJobs(params)
            .then((r) => r.json())
            .then((d) => (d.status ? d.data.counts?.[id] : undefined))
            .catch(() => undefined);
        })
      );
      if (cancelled) return;
      setCounts((prev) => {
        const next = { ...prev };
        PAGE_TABS.forEach(({ id }, idx) => {
          if (results[idx] !== undefined) next[id] = results[idx];
        });
        return next;
      });
    };
    load();
    return () => { cancelled = true; };
  }, [topTab, activeTab, overviewDateRange, refreshTick]);

  /* ── Client-side search filtering ── */
  const filteredJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((job) => {
      const haystack = `${job.title} ${job.location} ${job.department} ${job.designation}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [jobs, searchQuery]);

  const hasActiveFilters = Boolean(searchQuery);

  const resetFilters = () => {
    setSearchQuery("");
  };

  /* ── Tab navigation ── */
  const handleTabChange = (tab) => navigate(tab.path);

  /* ── Create / Edit modal handlers ── */
  const openCreateModal  = () => { setEditJob(null); setIsCreateModalOpen(true); };
  const openEditModal    = async (job) => {
    try {
      const res  = await getHiringJobById(job.id);
      const data = await res.json();
      setEditJob(data.status ? normalizeJob(data.data) : job);
    } catch {
      setEditJob(job);
    }
    setIsCreateModalOpen(true);
  };
  const closeCreateModal = () => { setIsCreateModalOpen(false); setEditJob(null); };

  /* ── Create as draft ── */
  const handleSaveDraft = async (formData) => {
    try {
      const res  = await createHiringJob({ ...formData, status: "DRAFT" });
      const data = await res.json();
      if (data.status) {
        toast.success("Job saved as draft");
        closeCreateModal();
        navigate("/hiring/draft-jobs");
        triggerRefresh();
      } else {
        toast.error(data.message || "Failed to save draft");
      }
    } catch {
      toast.error("Failed to save draft");
    }
  };

  /* ── Create and publish ── */
  const handlePublish = async (formData) => {
    try {
      const res  = await createHiringJob({ ...formData, status: "OPEN" });
      const data = await res.json();
      if (data.status) {
        toast.success("Job published successfully");
        closeCreateModal();
        navigate("/hiring/open-jobs");
        triggerRefresh();
      } else {
        toast.error(data.message || "Failed to publish job");
      }
    } catch {
      toast.error("Failed to publish job");
    }
  };

  /* ── Update existing job fields ── */
  const handleUpdateJob = async (formData) => {
    try {
      const res  = await updateHiringJob(editJob.id, formData);
      const data = await res.json();
      if (data.status) {
        toast.success("Job updated successfully");
        closeCreateModal();
        triggerRefresh();
      } else {
        setErrorModal({
          open: true,
          message: data.message || "Update failed",
        });
      }
    } catch {
      setErrorModal({ open: true, message: "Update failed" });
    }
  };

  const closeErrorModal = () => setErrorModal({ open: false, message: "" });

  /* ── Confirm modal helpers ── */
  const openConfirm  = (type, job) => setConfirmModal({ open: true, type, job });
  const closeConfirm = () => setConfirmModal({ open: false, type: null, job: null });

  /* ── Status transitions ── */
  const handleCloseJob = async () => {
    try {
      const res  = await updateHiringJobStatus(confirmModal.job.id, "CLOSED");
      const data = await res.json();
      if (data.status) {
        toast.success("Job closed");
        closeConfirm();
        navigate("/hiring/closed-jobs");
      } else {
        toast.error(data.message || "Failed to close job");
      }
    } catch {
      toast.error("Failed to close job");
    }
  };

  const handleReopenJob = async () => {
    try {
      const res  = await updateHiringJobStatus(confirmModal.job.id, "OPEN");
      const data = await res.json();
      if (data.status) {
        toast.success("Job reopened");
        closeConfirm();
        navigate("/hiring/open-jobs");
      } else {
        toast.error(data.message || "Failed to reopen job");
      }
    } catch {
      toast.error("Failed to reopen job");
    }
  };

  const confirmPublishDraft = async () => {
    try {
      const res  = await updateHiringJobStatus(confirmModal.job.id, "OPEN");
      const data = await res.json();
      if (data.status) {
        toast.success("Job published successfully");
        closeConfirm();
        navigate("/hiring/open-jobs");
      } else {
        toast.error(data.message || "Failed to publish job");
      }
    } catch {
      toast.error("Failed to publish job");
    }
  };

  const handleDeleteJob = async () => {
    try {
      const res  = await deleteHiringJob(confirmModal.job.id);
      const data = await res.json();
      if (data.status) {
        toast.success("Job deleted successfully");
        closeConfirm();
        triggerRefresh();
      } else {
        toast.error(data.message || "Failed to delete job");
      }
    } catch {
      toast.error("Failed to delete job");
    }
  };

  const handlePublishDraft = (job) => openConfirm("PUBLISH", job);

  /* ── Confirm modal config ── */
  const CONFIRM_CONFIG = {
    CLOSE:   { title: "Close Job",    message: `Are you sure you want to close "${confirmModal.job?.title}"? No new applications will be accepted.`, action: handleCloseJob,       actionLabel: "Close Job" },
    DELETE:  { title: "Delete Job",   message: `Are you sure you want to permanently delete "${confirmModal.job?.title}"?`,                          action: handleDeleteJob,      actionLabel: "Delete" },
    REOPEN:  { title: "Reopen Job",   message: `Are you sure you want to reopen "${confirmModal.job?.title}" for new applications?`,                 action: handleReopenJob,      actionLabel: "Reopen" },
    PUBLISH: { title: "Publish Job",  message: `Are you sure you want to publish "${confirmModal.job?.title}"?`,                                     action: confirmPublishDraft,  actionLabel: "Publish" },
  };

  const confirm = confirmModal.type ? CONFIRM_CONFIG[confirmModal.type] : null;

  const goToJob = (job) =>
    navigate(`/hiring/job/${job.id}`, { state: { fromHiringTab: activeTab } });

  const resetKey = [activeTab, searchQuery, overviewDateRange.fromDate, overviewDateRange.toDate].join("|");
  const emptyCopy = EMPTY_STATE_COPY[activeTab];

  // Permissions are already loaded before this component mounts (App gates the
  // whole router on it), so these decisions are stable on first render.
  if (!hasHiringManagement) {
    return <Navigate to="/hiring/openings" replace />;
  }
  // Landed on /hiring/all-referrals without hiring.view (stale link, permission
  // removed) → fall back to Job Openings instead of a 403 tab.
  if (topTab === "ALL_REFERRALS" && !canSeeAllReferrals) {
    return <Navigate to="/hiring/open-jobs" replace />;
  }

  return (
    <>
      <Header />
      <div
        className="d-flex"
        style={{
          "--hiring-primary": primaryColor,
          "--hiring-bg": "#F8FAFC",
          "--hiring-card": "#FFFFFF",
          "--hiring-border": "#E5E7EB",
          "--hiring-text": "#111827",
          "--hiring-text-secondary": "#6B7280",
          "--hiring-success": "#16A34A",
          "--hiring-warning": "#F59E0B",
          "--hiring-danger": "#DC2626",
        }}
      >
        <LeftNavBar />
        <div className="page-content-wrapper">

          {/* Page Header */}
          <PageHeader>
            <PageHeaderName>Hiring</PageHeaderName>
            <div style={{ marginRight: "20px" }} className="text-end d-flex align-items-center">
              {canCreateJob && (
                <PrimaryButton onClick={openCreateModal}>+ Create Job</PrimaryButton>
              )}
            </div>
          </PageHeader>

          {/* Aggregate dashboard — drives the shared date window for the job table */}
          <HiringOverview
            refreshTick={refreshTick}
            onDateRangeChange={handleOverviewDateRangeChange}
          />

          {/* Top-level tabs — Job Openings vs the two referral views */}
          <div className="hiring-tab-bar hiring-top-tab-bar">
            {visibleTopTabs.map((tab) => (
              <button
                key={tab.id}
                style={
                  topTab === tab.id
                    ? { color: primaryColor, borderBottom: `3px solid ${primaryColor}` }
                    : {}
                }
                className={topTab === tab.id ? "hiring-active-tab" : "hiring-tab"}
                onClick={() => handleTabChange(tab)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {topTab !== "JOBS" ? (
            <div key={topTab} className="hiring-tab-content-fade">
              <MyReferralsList scope={topTab === "ALL_REFERRALS" ? "all" : "mine"} />
            </div>
          ) : (
            /* Content area */
            <div key={`JOBS-${activeTab}`} className="hiring-content-box hiring-tab-content-fade">

              {/* Job status sub-tabs */}
              <div className="hiring-tab-bar">
                {PAGE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    style={
                      activeTab === tab.id
                        ? { color: primaryColor, borderBottom: `3px solid ${primaryColor}` }
                        : {}
                    }
                    className={activeTab === tab.id ? "hiring-active-tab" : "hiring-tab"}
                    onClick={() => handleTabChange(tab)}
                  >
                    {tab.label}
                    <span
                      style={{
                        marginLeft: "6px",
                        fontSize: "12px",
                        color: activeTab === tab.id ? primaryColor : "#9e9e9e",
                      }}
                    >
                      ({counts[tab.id] || 0})
                    </span>
                  </button>
                ))}
              </div>

              {/* Search only — date range is controlled by Hiring Overview above */}
              <div className="hiring-filters-bar">
                <div className="hiring-search-box">
                  <MdSearch className="hiring-search-icon" />
                  <input
                    type="text"
                    className="hiring-search-input"
                    placeholder="Search by title, location, or designation"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {hasActiveFilters && (
                  <button type="button" className="hiring-reset-btn" onClick={resetFilters}>
                    <MdClose size={14} /> Clear Search
                  </button>
                )}
              </div>

              {/* Jobs table */}
              <div className="hiring-table-wrapper">
                <JobsTable
                  jobs={filteredJobs}
                  loading={loading}
                  resetKey={resetKey}
                  onView={goToJob}
                  onEdit={openEditModal}
                  onClose={(j) => openConfirm("CLOSE", j)}
                  onPublish={handlePublishDraft}
                  onDelete={(j) => openConfirm("DELETE", j)}
                  onReopen={(j) => openConfirm("REOPEN", j)}
                  emptyTitle={emptyCopy.title}
                  emptyMessage={emptyCopy.message}
                />
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Create / Edit Job — SlidingModal */}
      <SlidingModal
        isModalOpen={isCreateModalOpen}
        modalName={editJob ? "Edit Job" : "Create Job"}
        closeBtn={closeCreateModal}
      >
        <CreateJobModal
          editJob={editJob}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onUpdate={handleUpdateJob}
          onCancel={closeCreateModal}
        />
      </SlidingModal>

      {/* Confirm Action — PopupModal */}
      {confirm && (
        <PopupModal showModal={confirmModal.open} modalName={confirm.title} closeBtn={closeConfirm}>
          <div className="hiring-confirm-modal">
            <p>{confirm.message}</p>
            <div className="hiring-confirm-modal-actions">
              <SecondaryButton onClick={closeConfirm}>Cancel</SecondaryButton>
              <PrimaryButton onClick={confirm.action}>{confirm.actionLabel}</PrimaryButton>
            </div>
          </div>
        </PopupModal>
      )}

      {/* Validation / update error — PopupModal */}
      <PopupModal
        showModal={errorModal.open}
        modalName="Unable to Update Job"
        closeBtn={closeErrorModal}
      >
        <div className="hiring-confirm-modal">
          <p>{errorModal.message}</p>
          <div className="hiring-confirm-modal-actions">
            <PrimaryButton onClick={closeErrorModal}>OK</PrimaryButton>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default Hiring;
